import React, { useState } from 'react';
import { db } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import './OxygenTankForm.css';

const oxygenTankSkills = [
  { id: 1, objective: 'Explain how to identify an Oxygen Tank', notes: ['O2 Tank is labeled oxygen', 'O2 Tank is green', 'O2 Tank and regulator use the Pin-Index Safety System'], critical: false },
  { id: 2, objective: 'Explain the purpose of the Regulator', notes: ['Regulates the pressure'], critical: false },
  { id: 3, objective: 'Explain the purpose Flowmeter', notes: ['Controls delivery and flow of oxygen'], critical: false },
  { id: 4, objective: 'Inspect the O-Ring', notes: ['Checking for presence and damages'], critical: true },
  { id: 5, objective: 'Attach Regulator to O2 Tank (hand tight)', notes: ['Must attach correctly, no leaks'], critical: true },
  { id: 6, objective: 'Open the Oxygen Tank properly', notes: ['All the way open, then a half-turn back'], critical: false },
  { id: 7, objective: 'Explain what makes the O2 Tank "full"', notes: ['Around 2,000 PSI'], critical: false },
  { id: 8, objective: 'States Minimum Oxygen Requirement', notes: ['500 PSI or greater'], critical: true },
];

const creditOptions = [
  { label: 'Full', value: 'full' },
  { label: 'Partial', value: 'partial' },
  { label: 'None', value: 'none' },
];

const CreditBox = ({ selected, type }) => {
  let boxClass = 'w-8 h-8 rounded border-2 border-primary cursor-pointer flex items-center justify-center';
  let inner = null;
  if (selected) {
    if (type === 'full') {
      inner = <div className="w-6 h-6 bg-green-400 rounded" />;
    } else if (type === 'partial') {
      inner = (
        <div className="w-6 h-6 bg-white rounded flex flex-col overflow-hidden">
          <div className="h-1/2 w-full bg-white" />
          <div className="h-1/2 w-full bg-green-400" />
        </div>
      );
    } else if (type === 'none') {
      inner = <div className="w-6 h-6 bg-red-400 rounded" />;
    }
  }
  return (
    <div className={boxClass}>{inner}</div>
  );
};

const OxygenTankForm = () => {
  const { currentUser } = useAuth();
  const [studentSignature, setStudentSignature] = useState('');
  const [instructorSignature, setInstructorSignature] = useState('');
  const [credit, setCredit] = useState(Array(oxygenTankSkills.length).fill(''));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Load previous submission if exists
  React.useEffect(() => {
    if (!currentUser) return;
    const fetchData = async () => {
      setLoading(true);
      const ref = doc(db, `skillSheets/${currentUser.uid}/oxygenTank/latest`);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setStudentSignature(data.studentSignature || '');
        setInstructorSignature(data.instructorSignature || '');
        setCredit(data.credit || Array(oxygenTankSkills.length).fill(''));
      }
      setLoading(false);
    };
    fetchData();
  }, [currentUser]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!currentUser) return;
    setSaving(true);
    setMessage('');
    try {
      const ref = doc(db, `skillSheets/${currentUser.uid}/oxygenTank/latest`);
      await setDoc(ref, {
        studentSignature,
        instructorSignature,
        credit,
        updatedAt: serverTimestamp(),
      });
      setMessage('Saved successfully!');
    } catch (err) {
      setMessage('Error saving. Please try again.');
    }
    setSaving(false);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-bg text-text flex flex-col items-center py-8">
      <h1 className="text-2xl font-bold mb-2 text-primary">Oxygen Tank Setup + O2 Administration</h1>
      <h2 className="text-lg font-semibold mb-4 text-secondary">OXYGEN TANK</h2>
      <form onSubmit={handleSubmit} className="w-full max-w-3xl bg-bg rounded-xl shadow p-4 border border-primary/40">
        <div className="rounded-xl border border-gray-400 mb-4 bg-table shadow">
          <table className="w-full table-auto">
            <thead>
              <tr className="bg-gradient-to-r from-primary/80 to-secondary/80 text-bg">
                <th className="p-2 bg-tableRow text-text">STUDENT OBJECTIVES</th>
                <th className="p-2 bg-tableRow text-text">INSTRUCTOR NOTES</th>
                <th className="p-2 bg-tableRow text-text" colSpan={3}>CREDIT</th>
              </tr>
              <tr className="bg-tableRow text-text">
                <th colSpan={2}></th>
                <th className="text-center font-semibold">Full</th>
                <th className="text-center font-semibold">Half</th>
                <th className="text-center font-semibold">None</th>
              </tr>
            </thead>
            <tbody>
              {oxygenTankSkills.map((skill, idx) => (
                <tr key={skill.id} className="bg-tableRow text-text border-b border-gray-300 last:border-0">
                  <td className={`p-2 align-middle${skill.critical ? ' text-red-600 font-bold' : ''}`}>{idx + 1}. {skill.objective}</td>
                  <td className="p-2 align-middle">{skill.notes.map((n, i) => <div key={i}>• {n}</div>)}</td>
                  <td className="p-2 align-middle text-center">
                    <button
                      type="button"
                      className="focus:outline-none"
                      onClick={() => setCredit(credit.map((c, i) => i === idx ? 'full' : c))}
                      aria-label="Full Credit"
                    >
                      <CreditBox selected={credit[idx] === 'full'} type="full" />
                    </button>
                  </td>
                  <td className="p-2 align-middle text-center">
                    <button
                      type="button"
                      className="focus:outline-none"
                      onClick={() => setCredit(credit.map((c, i) => i === idx ? 'partial' : c))}
                      aria-label="Half Credit"
                    >
                      <CreditBox selected={credit[idx] === 'partial'} type="partial" />
                    </button>
                  </td>
                  <td className="p-2 align-middle text-center">
                    <button
                      type="button"
                      className="focus:outline-none"
                      onClick={() => setCredit(credit.map((c, i) => i === idx ? 'none' : c))}
                      aria-label="No Credit"
                    >
                      <CreditBox selected={credit[idx] === 'none'} type="none" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-2 mb-4">
          <label className="font-medium">Student Signature:
            <input
              type="text"
              value={studentSignature}
              onChange={(e) => setStudentSignature(e.target.value)}
              className="border rounded px-2 py-1 bg-bg text-text border-primary ml-2"
            />
          </label>
          <label className="font-medium">Instructor Signature:
            <input
              type="text"
              value={instructorSignature}
              readOnly
              className="border rounded px-2 py-1 bg-bg text-text border-primary ml-2"
            />
          </label>
        </div>
        <button type="submit" className="bg-primary text-white px-4 py-2 rounded hover:bg-secondary transition-colors" disabled={saving}>
          {saving ? 'Saving...' : 'Submit'}
        </button>
        {message && <div className="mt-4 text-center text-red-500">{message}</div>}
      </form>
    </div>
  );
};

export default OxygenTankForm;
